import { useCallback, useEffect, useMemo, useState } from 'react';
import { Case, Counselor, Mentorship, User } from '../types';
import {
  MentorshipError,
  activeMentorshipForApprentice,
  activeMentorshipsForMentor,
  completeMentorship,
  createMentorship,
  deleteMentorship,
  fetchAllMentorships,
  reopenMentorship,
  setMentorshipCases,
  setUserAsMentor,
  unsetUserAsMentor,
} from '../utils/mentorship';
import { findCounselorForUser } from '../components/Admin/adminUtils';
import { t } from '../utils/translations';

interface UseMentorshipDataInput {
  users: User[];
  counselors: Counselor[];
  allCases: Case[];
  currentUserId: string | undefined;
  currentUserName: string | undefined;
}

export function useMentorshipData({
  users,
  counselors,
  allCases,
  currentUserId,
  currentUserName,
}: UseMentorshipDataInput) {
  const [mentorships, setMentorships] = useState<Mentorship[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      setError(null);
      setMentorships(await fetchAllMentorships());
    } catch (err) {
      console.error('Error loading mentorships:', err);
      setError(t.admin.mentorship.loadError);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const activeUsers = useMemo(() => users.filter((u) => u.isActive !== false), [users]);

  const mentors = useMemo(
    () => activeUsers.filter((u) => u.isMentor === true),
    [activeUsers]
  );

  /**
   * Eligible mentors: active, not already a mentor, and not currently somebody's
   * apprentice — a user is one or the other, never both.
   */
  const mentorCandidates = useMemo(
    () =>
      activeUsers.filter(
        (u) =>
          u.isMentor !== true && activeMentorshipForApprentice(u.id, mentorships) === null
      ),
    [activeUsers, mentorships]
  );

  /** Eligible apprentices: active, not a mentor, without an active pairing. */
  const apprenticeCandidates = useMemo(
    () =>
      activeUsers.filter(
        (u) =>
          u.isMentor !== true && activeMentorshipForApprentice(u.id, mentorships) === null
      ),
    [activeUsers, mentorships]
  );

  const mentorshipsByMentor = useMemo(() => {
    const map = new Map<string, Mentorship[]>();
    mentorships.forEach((mentorship) => {
      const list = map.get(mentorship.mentorUserId) ?? [];
      list.push(mentorship);
      map.set(mentorship.mentorUserId, list);
    });
    return map;
  }, [mentorships]);

  /**
   * Cases belonging to this mentor. Two things make the naive lookup fail:
   * the counselor profile may have no `linkedUserId` (older accounts are only
   * matchable by email), and `assignedCounselorId` holds the counselor doc id
   * on some cases but the plain user id on others — which is exactly why
   * isCaseVisibleToCounselor carries the same fallback.
   */
  const casesForMentor = useCallback(
    (mentorUserId: string): Case[] => {
      const mentor = users.find((u) => u.id === mentorUserId);
      const counselor =
        findCounselorForUser(counselors, mentorUserId) ??
        (mentor?.email
          ? counselors.find((c) => c.email === mentor.email)
          : undefined);

      const ownerIds = new Set<string>([mentorUserId]);
      if (counselor) ownerIds.add(counselor.id);

      return allCases.filter(
        (c) =>
          (c.assignedCounselorId && ownerIds.has(c.assignedCounselorId)) ||
          (c.proposedCounselorId && ownerIds.has(c.proposedCounselorId))
      );
    },
    [counselors, allCases, users]
  );

  /**
   * Granted cases that are no longer assigned to the mentor — reassignment does
   * not revoke the apprentice, but the leader should see the drift.
   */
  const strandedCaseIds = useCallback(
    (mentorship: Mentorship): string[] => {
      const owned = new Set(casesForMentor(mentorship.mentorUserId).map((c) => c.id));
      return mentorship.caseIds.filter((caseId) => !owned.has(caseId));
    },
    [casesForMentor]
  );

  const caseTitleById = useMemo(() => {
    const map = new Map<string, string>();
    allCases.forEach((c) => map.set(c.id, c.title));
    return map;
  }, [allCases]);

  const run = useCallback(
    async (id: string, action: () => Promise<void>) => {
      setBusyId(id);
      try {
        await action();
        await reload();
        return { ok: true as const };
      } catch (err) {
        if (err instanceof MentorshipError) {
          return { ok: false as const, message: err.message };
        }
        console.error('Mentorship action failed:', err);
        return { ok: false as const, message: null };
      } finally {
        setBusyId(null);
      }
    },
    [reload]
  );

  const markMentor = useCallback(
    (userId: string) => run(userId, () => setUserAsMentor(userId)),
    [run]
  );

  const unmarkMentor = useCallback(
    (userId: string) => run(userId, () => unsetUserAsMentor(userId, mentorships)),
    [run, mentorships]
  );

  const addApprentice = useCallback(
    (mentorUserId: string, apprenticeUserId: string) => {
      const mentor = users.find((u) => u.id === mentorUserId);
      const apprentice = users.find((u) => u.id === apprenticeUserId);
      if (!mentor || !apprentice || !currentUserId) {
        return Promise.resolve({ ok: false as const, message: null });
      }
      return run(mentorUserId, () =>
        createMentorship({
          mentor,
          apprentice,
          createdBy: currentUserId,
          createdByName: currentUserName || '',
        })
      );
    },
    [run, users, currentUserId, currentUserName]
  );

  const saveGrantedCases = useCallback(
    (mentorshipId: string, caseIds: string[]) =>
      run(mentorshipId, () => setMentorshipCases(mentorshipId, caseIds)),
    [run]
  );

  const finishMentorship = useCallback(
    (mentorshipId: string) => run(mentorshipId, () => completeMentorship(mentorshipId)),
    [run]
  );

  const removeApprentice = useCallback(
    (mentorshipId: string) => run(mentorshipId, () => deleteMentorship(mentorshipId)),
    [run]
  );

  const restartMentorship = useCallback(
    (mentorshipId: string) => run(mentorshipId, () => reopenMentorship(mentorshipId)),
    [run]
  );

  return {
    mentorships,
    mentors,
    mentorCandidates,
    apprenticeCandidates,
    mentorshipsByMentor,
    casesForMentor,
    strandedCaseIds,
    caseTitleById,
    activeMentorshipsForMentor,
    loading,
    error,
    busyId,
    markMentor,
    unmarkMentor,
    addApprentice,
    saveGrantedCases,
    finishMentorship,
    removeApprentice,
    restartMentorship,
    reload,
  };
}
